/**
 * i18n bootstrap — static UI strings for English, Spanish, French, German,
 * Portuguese. English keys act as fallback. Dynamic content (lesson bodies,
 * community posts) is translated at runtime via `useAutoTranslate` which
 * calls `POST /api/translate` (Claude Haiku 4.5) whenever the selected
 * language is not English.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const commonEn = {
  dashboard: 'Dashboard',
  learning:  'Learning',
  community: 'Community',
  network:   'My Network',
  more:      'More',
  login:     'Log in',
  logout:    'Log out',
  redeem:    'Redeem code',
  terms:     'Terms of Use',
  community_standards: 'Community Standards',
  report_a_bug: 'Report a bug',
  language:  'Language',
  translated: 'Translated',
  translating: 'Translating…',
  show_original: 'Show original',
};

const resources = {
  en: {
    translation: {
      common: commonEn,
    },
  },
  es: {
    translation: {
      common: {
        dashboard: 'Panel',
        learning:  'Aprendizaje',
        community: 'Comunidad',
        network:   'Mi red',
        more:      'Más',
        login:     'Iniciar sesión',
        logout:    'Cerrar sesión',
        redeem:    'Canjear código',
        terms:     'Términos de uso',
        community_standards: 'Normas de la comunidad',
        report_a_bug: 'Reportar un error',
        language:  'Idioma',
        translated: 'Traducido',
        translating: 'Traduciendo…',
        show_original: 'Ver original',
      },
    },
  },
  fr: {
    translation: {
      common: {
        dashboard: 'Tableau de bord',
        learning:  'Apprentissage',
        community: 'Communauté',
        network:   'Mon réseau',
        more:      'Plus',
        login:     'Se connecter',
        logout:    'Déconnexion',
        redeem:    'Utiliser un code',
        terms:     'Conditions d\'utilisation',
        community_standards: 'Normes de la communauté',
        report_a_bug: 'Signaler un bug',
        language:  'Langue',
        translated: 'Traduit',
        translating: 'Traduction…',
        show_original: 'Voir l\'original',
      },
    },
  },
  de: {
    translation: {
      common: {
        dashboard: 'Übersicht',
        learning:  'Lernen',
        community: 'Community',
        network:   'Mein Netzwerk',
        more:      'Mehr',
        login:     'Anmelden',
        logout:    'Abmelden',
        redeem:    'Code einlösen',
        terms:     'Nutzungsbedingungen',
        community_standards: 'Community-Richtlinien',
        report_a_bug: 'Fehler melden',
        language:  'Sprache',
        translated: 'Übersetzt',
        translating: 'Übersetze…',
        show_original: 'Original anzeigen',
      },
    },
  },
  pt: {
    translation: {
      common: {
        dashboard: 'Painel',
        learning:  'Aprendizado',
        community: 'Comunidade',
        network:   'Minha rede',
        more:      'Mais',
        login:     'Entrar',
        logout:    'Sair',
        redeem:    'Resgatar código',
        terms:     'Termos de uso',
        community_standards: 'Diretrizes da comunidade',
        report_a_bug: 'Reportar bug',
        language:  'Idioma',
        translated: 'Traduzido',
        translating: 'Traduzindo…',
        show_original: 'Ver original',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'fda_lang',
      caches: ['localStorage'],
    },
  });

export default i18n;
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
];

/** Map an i18n code (from `i18n.language`) to the canonical English name
 *  the backend uses as the target language. */
export const LANG_CODE_TO_NAME = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  pt: 'Portuguese',
};
